import React, {useCallback, useEffect, useState} from 'react';
import cx from 'classnames';

export const useOutOfViewNavigation = ({
    content,
    root,
    containerClassName,
    isDisabled = false,
    moveBy = 60,
}: {
    isDisabled?: boolean;
    content: React.ReactElement;
    root: HTMLElement | null;
    moveBy?: number;
    containerClassName?: string;
}) => {
    const [translateX, setTranslateX] = useState(0);
    const [moveLeft, setMoveLeft] = useState<(() => void) | undefined>(
        undefined,
    );
    const [moveRight, setMoveRight] = useState<(() => void) | undefined>(
        undefined,
    );
    const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
    const [startEl, setStartEl] = useState<HTMLDivElement | null>(null);
    const [endEl, setEndEl] = useState<HTMLDivElement | null>(null);

    const setContainerRef = useCallback((node: HTMLDivElement | null) => {
        setContainerEl(node);
    }, []);

    const moveLeftFn = useCallback(() => {
        setTranslateX(value => value + moveBy);
    }, [moveBy]);

    const moveRightFn = useCallback(() => {
        setTranslateX(value => value - moveBy);
    }, [moveBy]);

    const setStartRef = useCallback((node: HTMLDivElement | null) => {
        setStartEl(node);
    }, []);

    const setEndRef = useCallback((node: HTMLDivElement | null) => {
        setEndEl(node);
    }, []);

    const navigationView = (
        <div
            style={{
                transform: `translateX(${translateX}px)`,
                transition: 'transform 300ms ease',
            }}
            ref={setContainerRef}
            className={cx(containerClassName)}
        >
            <div ref={setStartRef} style={{width: '1px', flexShrink: 0}} />
            {content}
            <div ref={setEndRef} style={{width: '1px', flexShrink: 0}} />
        </div>
    );

    useEffect(() => {
        if (isDisabled || !root || !containerEl || !startEl || !endEl) {
            return;
        }

        const observer = new IntersectionObserver(
            entries => {
                for (const entry of entries) {
                    if (entry.target === startEl) {
                        setMoveLeft(() =>
                            !entry.isIntersecting ? moveLeftFn : undefined,
                        );
                    }

                    if (entry.target === endEl) {
                        setMoveRight(() =>
                            !entry.isIntersecting ? moveRightFn : undefined,
                        );
                    }
                }
            },
            {
                root,
                threshold: 0.99,
            },
        );

        observer.observe(startEl);
        observer.observe(endEl);

        return () => observer.disconnect();
    }, [
        root,
        containerEl,
        startEl,
        endEl,
        isDisabled,
        moveLeftFn,
        moveRightFn,
    ]);

    useEffect(() => {
        const parentContainer = containerEl?.parentElement;
        if (!parentContainer) {
            return;
        }

        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                const parentWidth = entry.contentRect.width;
                const containerWidth =
                    containerEl.getBoundingClientRect().width;
                if (containerWidth < parentWidth && translateX !== 0) {
                    setTranslateX(0);
                }
            }
        });

        observer.observe(parentContainer);

        return () => observer.disconnect();
    }, [containerEl, translateX]);

    const hasOverflowingContent = !!moveLeft || !!moveRight;

    useEffect(() => {
        /*
         * If there was overflowing content previously but it changes to not be
         * overflowing any longer by a dynamic change we need to reset the translate position
         * to its original place.
         */
        if (!hasOverflowingContent && translateX !== 0) {
            setTranslateX(0);
        }
    }, [hasOverflowingContent, translateX]);

    return {
        navigationView,
        moveLeft,
        moveRight,
        hasOverflowingContent,
    };
};
